/**
 * Single insight card — styled per category (Alerts, Progress, Opportunities, Analysis).
 */
import React from 'react';
import {
  AlertTriangle,
  Lightbulb,
  BarChart3,
  RefreshCw,
  CheckCircle2,
  Clock,
  ThumbsUp,
  ThumbsDown,
  X,
  ChevronRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { InsightCategory } from '../../types/insights.types';
import {
  resolveInsightTitle,
  resolveInsightTypeBadge,
  resolveInsightBody,
  formatInsightNumbers,
} from '../../utils/insight-display-utils';

export interface InsightItemShape {
  id?: string;
  title?: string;
  goal_description?: string;
  explanation?: string;
  summary?: string;
  rationale?: string;
  current_status?: string;
  confidence?: number;
  confidence_score?: number;
  type?: string;
  indicator?: string;
  estimated_impact?: string;
  potential_impact?: string;
  impact?: string;
  action?: string;
  suggested_next_steps?: string[];
  recommended_actions?: string[];
  categories?: string[];
  drivers?: { category?: string; period?: string };
}

interface InsightCardProps {
  item: InsightItemShape;
  category: InsightCategory;
  onViewDetails?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onFeedback?: (
    insightId: string,
    feedbackType: 'helpful' | 'not_relevant'
  ) => Promise<boolean>;
  onAction?: (item: InsightItemShape) => void;
  onRefresh?: () => void;
  refreshedAt: string | null;
  userId: string | null;
  categoryNameMap?: Map<string, string>;
  initialFeedback?: 'helpful' | 'not_relevant' | null;
  alertActive?: boolean;
}

function defaultActionLabel(category: InsightCategory, item: InsightItemShape): string {
  if (item.action) return item.action;
  if (category === 'opportunities') {
    const step = item.suggested_next_steps?.[0];
    if (step?.toLowerCase().includes('review')) return 'Review Subs';
    return 'Review';
  }
  if (category === 'alerts') return 'Set Alert';
  return 'See details';
}

export function InsightCard({
  item,
  category,
  onViewDetails,
  onDismiss,
  onFeedback,
  onAction,
  onRefresh,
  refreshedAt,
  userId,
  categoryNameMap,
  initialFeedback = null,
  alertActive = false,
}: InsightCardProps): React.ReactElement {
  const [feedbackSent, setFeedbackSent] = React.useState<'helpful' | 'not_relevant' | null>(
    initialFeedback
  );
  const [feedbackError, setFeedbackError] = React.useState<string | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = React.useState(false);

  React.useEffect(() => {
    setFeedbackSent(initialFeedback);
  }, [initialFeedback, item.id]);

  const impact = formatInsightNumbers(
    item.estimated_impact ?? item.potential_impact ?? item.impact
  );
  const displayTitle = resolveInsightTitle(item, categoryNameMap);
  const categoryTag =
    resolveInsightTypeBadge(item) ||
    (category === 'progress'
      ? 'Savings Progress'
      : category === 'opportunities'
        ? 'Cost Optimisation'
        : category === 'alerts'
          ? 'Spending Alert'
          : 'Analysis');
  const body = resolveInsightBody(item, categoryNameMap);
  const actionLabel = defaultActionLabel(category, item);
  const isActionPrimary =
    category === 'alerts' &&
    (actionLabel === 'Set Alert' || actionLabel === 'Plan Buffer' || actionLabel === 'Review Now');

  const isAlert = category === 'alerts';
  const isProgress = category === 'progress';
  const isOpportunity = category === 'opportunities';

  const bgClass = isAlert
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    : isProgress
      ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
      : isOpportunity
        ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800'
        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';

  const badgeClass = isAlert
    ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
    : isProgress
      ? 'bg-emerald-200/80 dark:bg-emerald-800/80 text-emerald-800 dark:text-emerald-200'
      : isOpportunity
        ? 'bg-violet-200/80 dark:bg-violet-800/80 text-violet-800 dark:text-violet-200'
        : 'bg-blue-200/80 dark:bg-blue-800/80 text-blue-800 dark:text-blue-200';

  const handleFeedback = async (feedbackType: 'helpful' | 'not_relevant') => {
    if (!userId || !item.id || feedbackSent || submittingFeedback) return;
    setFeedbackError(null);
    setSubmittingFeedback(true);
    try {
      const ok = (await onFeedback?.(item.id, feedbackType)) ?? false;
      if (ok) {
        setFeedbackSent(feedbackType);
      } else {
        setFeedbackError('Could not save feedback. Please try again.');
      }
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleActionClick = () => {
    if (isActionPrimary && onAction) {
      onAction(item);
      return;
    }
    onViewDetails?.(item.id ?? '');
  };

  const Icon = isAlert
    ? AlertTriangle
    : isProgress
      ? CheckCircle2
      : isOpportunity
        ? Lightbulb
        : BarChart3;

  const iconWrapClass = isAlert
    ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
    : isProgress
      ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300'
      : isOpportunity
        ? 'bg-violet-200 dark:bg-violet-800 text-violet-700 dark:text-violet-300'
        : 'bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300';

  return (
    <div
      className={`rounded-xl border p-4 ${bgClass}`}
      data-testid={`insight-card-${category}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${iconWrapClass}`}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {displayTitle}
              </span>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}
              >
                {categoryTag}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{body}</p>
            {impact && (
              <p
                className={`mt-2 text-sm font-medium ${
                  isAlert
                    ? 'text-red-700 dark:text-red-400'
                    : 'text-emerald-700 dark:text-emerald-400'
                }`}
              >
                Potential Impact: {impact}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col items-end gap-2 self-start">
          <div className="flex items-center gap-1">
            {onDismiss && (
              <button
                type="button"
                onClick={() => onDismiss(item.id ?? '')}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200/80 dark:hover:bg-gray-600 focus-ring"
                aria-label="Dismiss this insight"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={handleActionClick}
              className={
                isActionPrimary
                  ? `inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium focus-ring ${
                      alertActive
                        ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`
                  : 'btn-secondary btn-sm inline-flex items-center gap-1'
              }
              aria-pressed={alertActive}
            >
              {alertActive && isActionPrimary ? 'Alert active' : actionLabel}
              {!isActionPrimary && <ChevronRight className="h-4 w-4" aria-hidden />}
            </button>
          </div>
        </div>
      </div>
      {userId && onFeedback && (
        <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-600/50 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Was this helpful?</span>
          <button
            type="button"
            onClick={() => void handleFeedback('helpful')}
            disabled={feedbackSent !== null || submittingFeedback}
            className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium focus-ring ${
              feedbackSent === 'helpful'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            aria-pressed={feedbackSent === 'helpful'}
            aria-label="Mark as helpful"
          >
            <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
            Helpful
          </button>
          <button
            type="button"
            onClick={() => void handleFeedback('not_relevant')}
            disabled={feedbackSent !== null || submittingFeedback}
            className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium focus-ring ${
              feedbackSent === 'not_relevant'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            aria-pressed={feedbackSent === 'not_relevant'}
            aria-label="Mark as not relevant"
          >
            <ThumbsDown className="h-3.5 w-3.5" aria-hidden />
            Not relevant
          </button>
          {feedbackError && (
            <span className="text-xs text-red-600 dark:text-red-400" role="alert">
              {feedbackError}
            </span>
          )}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-600/50 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" aria-hidden />
          Insights last updated:{' '}
          {refreshedAt
            ? formatDistanceToNow(new Date(refreshedAt), { addSuffix: true })
            : 'never'}
        </span>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline focus-ring rounded"
            aria-label="Refresh analysis"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Refresh Analysis
          </button>
        )}
      </div>
    </div>
  );
}
